<template>
  <Modal
    :title="`Exit ${count} ${noun}${count !== 1 ? 's' : ''} from play`"
    :max-width="480"
    :error="error"
    @close="emit('close')"
  >
    <div class="exitBody">
      <p class="exitIntro">Choose how to exit {{ count === 1 ? `this ${noun}` : `these ${noun}s` }} from the play. Any active sequence enrollments will be terminated.</p>

      <div class="optionList">
        <label
          v-for="opt in EXIT_OPTIONS"
          :key="opt.value"
          class="optionRow"
          :class="{ selected: selected === opt.value }"
        >
          <input
            type="radio"
            :value="opt.value"
            v-model="selected"
            class="optionRadio"
          />
          <div class="optionText">
            <div class="optionLabel">{{ opt.label }}</div>
            <div class="optionDesc">{{ opt.description }}</div>
          </div>
        </label>
      </div>

      <label class="notesLabel">
        <span class="notesTitle">Notes <span class="notesOptional">(optional)</span></span>
        <textarea v-model="notes" class="notesInput" rows="2" :placeholder="`Add context for why these ${noun}s are being exited…`" />
      </label>
    </div>

    <template #footer>
      <button class="btn secondary" :disabled="saving" @click="emit('close')">Cancel</button>
      <button class="btn danger" :disabled="!selected || saving" @click="submit">
        {{ saving ? 'Exiting…' : 'Exit from play' }}
      </button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import Modal from './Modal.vue'
import { apiFetch } from '../../services/api'

const EXIT_OPTIONS = [
  { value: 'engaged', label: 'Engaged', description: 'Contact has responded or shown interest' },
  { value: 'timed_out', label: 'Timed Out', description: 'No response within the expected timeframe' },
  { value: 'disqualified', label: 'Disqualified', description: 'Not a fit for this play' },
]

const props = withDefaults(
  defineProps<{
    membershipIds: string[]
    /** What the rows are called on the calling surface ("candidate", "contact"). */
    noun?: string
  }>(),
  { noun: 'candidate' },
)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'exited', exitReason: string, result: BulkExitResult): void
}>()

/** Per-membership outcome from the server — a batch can partly fail. */
export interface BulkExitResult {
  exited: number
  failed: number
}

const count = props.membershipIds.length

const selected = ref<string>('')
const notes = ref('')
const saving = ref(false)
const error = ref('')

async function submit() {
  if (!selected.value || saving.value) return
  saving.value = true
  error.value = ''
  try {
    const resp: any = await apiFetch('/sales-plays/memberships/bulk-exit', {
      method: 'POST',
      body: {
        membership_ids: props.membershipIds,
        exit_reason: selected.value,
        exit_notes: notes.value.trim() || null,
      },
    })
    emit('exited', selected.value, {
      exited: resp?.exited?.length ?? 0,
      failed: resp?.failed?.length ?? 0,
    })
    emit('close')
  } catch (e: any) {
    error.value = e?.message || 'Failed to exit candidates'
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.exitBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.exitIntro {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
  line-height: var(--lh-normal);
}

.optionList {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.optionRow {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: border-color 0.1s, background 0.1s;
}
.optionRow:hover {
  background: var(--color-bg-subtle);
}
.optionRow.selected {
  border-color: var(--color-accent);
  background: var(--color-accent-soft);
}

.optionRadio {
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: var(--color-accent);
}

.optionText {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.optionLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}
.optionDesc {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
}

.notesLabel {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
.notesTitle {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
}
.notesOptional {
  font-weight: var(--fw-regular);
  color: var(--color-text-tertiary);
  text-transform: none;
  letter-spacing: normal;
}
.notesInput {
  width: 100%;
  resize: vertical;
  padding: var(--space-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
}
</style>
